<div class="wrapper">
  <!-- The drawings detail header (Figma 33128:76594): ‹ back, the set's name, its page count, its status. The ••• holds
       Discard set while the set is a draft; Publish stays the footer's primary button. -->
  <!-- A set made by Upload new revision names its target in the title (W65), so no line repeats it -->
  <app-drawings-page-header [title]="reviewTitle" [subtitle]="set ? (set.revisions?.length ?? 0) + ' pages' : ''"
    backLabel="Back to Drawings" backTestId="drawing-set-back" titleTestId="drawing-set-title" (back)="back.emit()">
    @if (canDiscard) {
      <button headerMenu type="button" mat-icon-button class="more-button" data-testid="drawing-set-more" aria-label="More actions" [matMenuTriggerFor]="setMenu"><mat-icon>more_horiz</mat-icon></button>
    }
    @if (firstIssueSummary) {
      <p headerSub data-testid="drawing-set-first-issue">{{ firstIssueSummary }}</p>
    }
    @if (setStatusChip; as chip) {
      <span headerChips class="status-badge" [ngClass]="'status-badge--' + chip.variant" data-testid="drawing-set-status">{{ chip.label }}</span>
    }
  </app-drawings-page-header>

  @if (carryCount > 0) {
    <div class="carry-banner mat-body-1" role="status" data-testid="drawing-carry-banner">
      <mat-icon>warning_amber</mat-icon>
      <span>{{ carryCount }} {{ carryCount === 1 ? 'redline' : 'redlines' }} on superseded sheets {{ carryCount === 1 ? 'needs' : 'need' }} review</span>
      <button type="button" class="link-button" (click)="reviewCarry.emit(setUuid)">Review redlines</button>
    </div>
  }

  @if (inspecting) {
    <div class="progress-line mat-body-1" role="status">
      <!-- api d3e5a155: by phase (title blocks, then matchlines with their own counts); Finishing… past the end -->
      <span>{{ inspectProgress.text }}</span>
      <div class="progress-track"><div class="progress-fill" [style.width.%]="inspectProgress.percent"></div></div>
    </div>
  } @else if (set?.inspect?.status === 'failed') {
    <div class="alert-error mat-body-1" role="alert">{{ publishBlockedReason }}</div>
  }
  @if (set?.inspect?.warning) {
    <div class="alert-warning mat-body-1" role="status" data-testid="drawing-set-inspect-warning">
      <mat-icon>warning_amber</mat-icon><span>{{ set.inspect.warning }}</span>
    </div>
  }
  @if (pollError) {
    <div class="alert-error alert-error--action mat-body-1" role="alert" data-testid="drawing-set-poll-error">
      <span>{{ pollError }}</span>
      <button type="button" class="link-button" (click)="retryPoll()">Retry</button>
    </div>
  }

  @if (showProblems) {
    <!-- W46: Figma page alert bar 17327:45382 (DESIGN.md alert_banner) -->
    <div class="problem-banner" role="status" data-testid="drawing-set-problem-banner">
      <mat-icon class="problem-banner-icon">warning_amber</mat-icon>
      <span class="problem-banner-text" data-testid="drawing-set-problem-text">{{ bannerText }}</span>
      <button type="button" class="link-button" data-testid="drawing-set-problem-review" (click)="reviewProblems('banner')">
        Review {{ attentionCount }} {{ attentionCount === 1 ? 'page' : 'pages' }}
      </button>
    </div>
  }

  @if (set && reviewRow; as row) {
    <!-- Sri: one page's review (a row opens it; a one-page replacement opens on it): the Overlay compare or the page -->
    <section class="page-review" data-testid="drawing-set-review-page" [attr.aria-label]="reviewPosition">
      <div class="review-bar">
        <button type="button" class="link-button" data-testid="drawing-set-review-back" (click)="backToPages()">‹ All pages</button>
        <div class="review-nav">
          <button type="button" class="nav-button" data-testid="drawing-set-review-prev" [disabled]="!reviewNeighbour(-1)" (click)="reviewStep(-1)">‹ Prev</button>
          <span class="review-position" data-testid="drawing-set-review-position">{{ reviewPosition }}</span>
          <button type="button" class="nav-button" data-testid="drawing-set-review-next" [disabled]="!reviewNeighbour(1)" (click)="reviewStep(1)">Next ›</button>
        </div>
        <span class="review-spacer"></span>
        <button type="button" class="link-button" data-testid="drawing-set-review-edit" (click)="openPage(row)">Edit details</button>
        @if (editable) {
          <button type="button" class="review-skip" data-testid="drawing-set-review-skip" (click)="setSkip(row, !isSkipped(row))">{{ isSkipped(row) ? 'Include page' : 'Skip page' }}</button>
        }
      </div>
      <div class="review-title">
        <span class="review-name">Page {{ row.page_number }}@if (row.number) { · {{ row.number }}}@if (row.title) { · {{ row.title }}}</span>
        <span class="review-chips" data-testid="drawing-set-review-chips">
          @if (isSkipped(row)) {
            <span class="status-badge status-badge--neutral">Skipped</span>
          } @else {
            <span class="chip" [class.chip--replaces]="row.match.kind === 'replaces'">{{ matchLabel(row) }}</span>
          }
          @for (w of warningTexts(row) ?? []; track w) { <span class="status-badge status-badge--warning">{{ w }}</span> }
        </span>
      </div>
      @if (row.match.kind === 'replaces' && row.match.sheetUuid) {
        @if (reviewAgainst) {
          <app-drawing-compare [projectId]="projectId" [revisionUuid]="row.uuid" [againstUuid]="reviewAgainst" [embedded]="true" [startMode]="'overlay'" />
        } @else {
          <p class="review-loading mat-body-1">Loading the current revision…</p>
        }
      } @else {
        <div class="review-page">
          @if (row.preview_url) {
            <img class="review-image" data-testid="drawing-set-review-image" [src]="row.preview_url" [alt]="'Page ' + row.page_number" />
          } @else {
            <p class="review-loading mat-body-1">This page has no preview yet.</p>
          }
        </div>
      }
    </section>
  } @else if (set && set.revisions?.length) {
    <div class="list-bar">
      <!-- The RFIs / Submittals list's filter pill (single choice) -->
      <mat-form-field class="filter-pill" [class.filter-pill--on]="filter === 'attention'" data-testid="drawing-set-filter">
        <mat-select [value]="filter" (selectionChange)="chooseFilter($event.value)" disableOptionCentering panelClass="rfi-sort-panel" aria-label="Pages to show">
          <mat-select-trigger>Show: {{ filter === 'attention' ? 'Needs attention (' + attentionCount + ')' : 'All pages (' + set.revisions.length + ')' }}</mat-select-trigger>
          <mat-option value="all" data-testid="drawing-set-filter-all">All pages ({{ set.revisions.length }})</mat-option>
          <mat-option value="attention" data-testid="drawing-set-filter-attention">Needs attention ({{ attentionCount }})</mat-option>
        </mat-select>
        <mat-icon matSuffix class="pill-chevron" svgIcon="chevron-down" aria-hidden="true" />
      </mat-form-field>
      <div class="view-toggle" role="group" aria-label="Layout">
        <button type="button" data-testid="drawing-set-view-list" [class.active]="viewMode === 'list'" [attr.aria-pressed]="viewMode === 'list'" (click)="setView('list')">List</button>
        <button type="button" data-testid="drawing-set-view-grid" [class.active]="viewMode === 'grid'" [attr.aria-pressed]="viewMode === 'grid'" (click)="setView('grid')">Grid</button>
      </div>
    </div>

    @if (canBulkEdit && checked.size) {
      <div class="bulk-bar" role="region" aria-label="Edit the selected pages" data-testid="drawing-set-bulk">
        <div class="bulk-line">
          <!-- Gmail style: the header box checks the pages on screen and offers the whole list; then says all are selected -->
          @if (moreThanOnScreen && setAllChecked) {
            <span class="mat-body-1 bulk-count" data-testid="drawing-set-bulk-count">All {{ visibleRows.length }} pages selected.</span>
          } @else if (moreThanOnScreen && pageAllChecked) {
            <span class="mat-body-1 bulk-count" data-testid="drawing-set-bulk-count">{{ pageRows.length }} pages on screen selected.</span>
            <button type="button" class="link-button" data-testid="drawing-set-check-all" (click)="checkAllInSet()">Select all {{ visibleRows.length }} pages</button>
          } @else {
            <span class="mat-body-1 bulk-count" data-testid="drawing-set-bulk-count">{{ checked.size }} selected</span>
          }
          <button type="button" class="link-button" data-testid="drawing-set-check-clear" [disabled]="bulkBusy" (click)="clearChecked()">Clear selection</button>
        </div>
        <div class="bulk-line bulk-fields">
          <label class="bulk-field">
            <span class="mat-caption bulk-label">Discipline</span>
            <input class="filter-input" maxlength="64" placeholder="Leave as is" data-testid="drawing-set-bulk-discipline" [value]="bulkDiscipline" [disabled]="bulkBusy"
              (input)="bulkDiscipline = $any($event.target).value" />
          </label>
          <label class="bulk-field">
            <span class="mat-caption bulk-label">Type</span>
            <select class="filter-input" data-testid="drawing-set-bulk-type" [disabled]="bulkBusy" (change)="bulkType = $any($event.target).value">
              <option value="" [selected]="!bulkType">Leave as is</option>
              @for (t of sheetTypes; track t.value) { <option [value]="t.value" [selected]="bulkType === t.value">{{ t.label }}</option> }
            </select>
          </label>
          <label class="bulk-field bulk-field--short">
            <span class="mat-caption bulk-label">Revision</span>
            <input class="filter-input" maxlength="32" placeholder="Leave as is" data-testid="drawing-set-bulk-label" [value]="bulkLabel" [disabled]="bulkBusy"
              (input)="bulkLabel = $any($event.target).value" />
          </label>
          <label class="bulk-field bulk-field--short">
            <!-- "Publish" is only the footer's action: per page it is Include / Skip -->
            <span class="mat-caption bulk-label">Include</span>
            <select class="filter-input" data-testid="drawing-set-bulk-publish" [disabled]="bulkBusy" (change)="bulkPublish = $any($event.target).value">
              <option value="" [selected]="!bulkPublish">Leave as is</option>
              <option value="publish" [selected]="bulkPublish === 'publish'">Include</option>
              <option value="skip" [selected]="bulkPublish === 'skip'">Skip</option>
            </select>
          </label>
          <button type="button" class="tool-button tool-button--on" data-testid="drawing-set-bulk-apply" [disabled]="bulkBusy || !hasBulkChange"
            (click)="applyBulk()">{{ bulkBusy ? 'Saving…' : 'Update ' + checked.size + (checked.size === 1 ? ' page' : ' pages') }}</button>
        </div>
        @if (bulkFailures.length) {
          <!-- The error message (Figma 13170:55541, change-orders upload-changes .errors): inline, about the checked pages -->
          <div class="bulk-failures" role="alert" data-testid="drawing-set-bulk-failures">
            <mat-icon class="bulk-failures-icon">error_outline</mat-icon>
            <div class="bulk-failures-body">
              <span data-testid="drawing-set-bulk-failure-summary">{{ bulkFailureSummary }}</span>
              <ul>
                @for (g of bulkFailureGroups; track g.message) {
                  <li data-testid="drawing-set-bulk-failure"><b>{{ g.pages.length === 1 ? 'Page' : 'Pages' }} {{ g.pages.join(', ') }}:</b> {{ g.message }}</li>
                }
              </ul>
              <div class="bulk-failures-actions">
                <button type="button" class="link-button" data-testid="drawing-set-bulk-retry" [disabled]="bulkBusy" (click)="retryBulkFailures()">Retry</button>
              </div>
            </div>
            <button type="button" mat-icon-button class="bulk-failures-close" aria-label="Dismiss" data-testid="drawing-set-bulk-dismiss" (click)="dismissBulkFailures()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
      </div>
    }

    @if (!visibleRows.length) {
      <p class="empty-state" data-testid="drawing-set-attention-empty">Nothing needs attention on these pages.</p>
    } @else if (viewMode === 'list') {
      <div class="table-scroll-wrapper">
        <table mat-table [dataSource]="pageRows" [trackBy]="trackRow" class="table" data-testid="drawing-set-table">
          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef>
              @if (canBulkEdit) {
                <mat-checkbox color="primary" aria-label="Select every page on this list page" data-testid="drawing-set-check-page" [checked]="pageAllChecked"
                  [indeterminate]="pageSomeChecked" (change)="togglePage($event.checked)"></mat-checkbox>
              }
            </th>
            <td mat-cell *matCellDef="let row">
              @if (canBulkEdit) {
                <mat-checkbox color="primary" data-testid="drawing-set-check" [attr.aria-label]="'Select page ' + row.page_number" [checked]="isChecked(row)"
                  (mousedown)="checkPress($event)" (keydown)="checkPress($event)" (change)="checkFromBox(row, $event.checked)"></mat-checkbox>
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="page">
            <th mat-header-cell *matHeaderCellDef>Page</th>
            <td mat-cell *matCellDef="let row" class="cell-page">{{ row.page_number }}</td>
          </ng-container>
          <ng-container matColumnDef="titleBlock">
            <th mat-header-cell *matHeaderCellDef><span class="visually-hidden">Title block</span></th>
            <td mat-cell *matCellDef="let row">
              <span class="tb-crop" data-testid="drawing-set-title-block">
                @if (row.title_block_url; as src) {
                  <!-- R56: the API's own title-block image, when inspection stored one -->
                  <img class="tb-image" [src]="src" alt="" loading="lazy" decoding="async" data-testid="drawing-set-title-block-image" />
                } @else if (row.preview_url; as src) {
                  @let crop = titleBlockCrop(row);
                  <img #cropImage [src]="src" alt="" loading="lazy" decoding="async" [style.width.%]="crop.width * 100" [style.left.%]="crop.left * 100" [style.top.%]="crop.top * 100"
                    (load)="onCropLoad(row, cropImage)" />
                } @else {
                  <span class="tb-crop-empty mat-caption">{{ inspecting ? 'Inspecting…' : '' }}</span>
                }
              </span>
            </td>
          </ng-container>
          <ng-container matColumnDef="number">
            <th mat-header-cell *matHeaderCellDef>Number</th>
            <td mat-cell *matCellDef="let row" class="cell-number">
              @if (row.number?.trim()) {
                <span class="mono">{{ row.number }}</span>
              } @else if (isSkipped(row)) {
                <span class="muted">—</span>
              } @else {
                <span class="required" data-testid="drawing-set-number-required">Required</span>
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="title">
            <th mat-header-cell *matHeaderCellDef>Title</th>
            <td mat-cell *matCellDef="let row" class="cell-title"><span class="truncate" [matTooltip]="row.title ?? ''" [matTooltipDisabled]="!row.title">{{ row.title || '—' }}</span></td>
          </ng-container>
          <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef>Type</th>
            <td mat-cell *matCellDef="let row">{{ typeLabel(row.sheet_type) }}</td>
          </ng-container>
          <ng-container matColumnDef="stations">
            <th mat-header-cell *matHeaderCellDef>Station</th>
            <td mat-cell *matCellDef="let row" class="cell-stations" data-testid="drawing-set-stations">{{ stationRangeText(row) || '—' }}</td>
          </ng-container>
          <ng-container matColumnDef="match">
            <th mat-header-cell *matHeaderCellDef>{{ exceptionsOnly ? '' : 'Match' }}</th>
            <td mat-cell *matCellDef="let row" class="cell-match">
              @if (isSkipped(row)) {
                <span class="status-badge status-badge--neutral" data-testid="drawing-set-badge-skipped">Skipped</span>
              } @else if (isFixed(row)) {
                <span class="status-badge status-badge--success" data-testid="drawing-set-badge-fixed">Fixed</span>
              } @else if (reasonText(row); as reason) {
                <span class="status-badge status-badge--neutral" data-testid="drawing-set-badge-reason" [matTooltip]="reason">{{ reason }}</span>
              } @else if (!exceptionsOnly) {
                <span class="chip" [class.chip--replaces]="row.match.kind === 'replaces'" [attr.data-testid]="row.sheet_pinned ? 'drawing-set-pinned' : null"
                  [matTooltip]="row.sheet_pinned ? 'Pinned to this sheet' : ''" [matTooltipDisabled]="!row.sheet_pinned">{{ matchLabel(row) }}</span>
              }
              @if (warningTexts(row); as warnings) {
                <!-- Wrong-drawing guard: never blocks; the publish asks to acknowledge them -->
                <div class="row-warnings">
                  @for (w of warnings; track w) { <span class="status-badge status-badge--warning" data-testid="drawing-set-warning" [matTooltip]="w">{{ w }}</span> }
                </div>
              }
              @if (row.inspect_warning) {
                <mat-icon class="warn-icon" [matTooltip]="row.inspect_warning" [attr.aria-label]="row.inspect_warning">warning_amber</mat-icon>
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row" class="cell-actions">
              @if (editable) {
                <button type="button" mat-icon-button [matMenuTriggerFor]="rowMenu" [attr.aria-label]="'Options for page ' + row.page_number"><mat-icon>more_horiz</mat-icon></button>
                <mat-menu #rowMenu="matMenu" class="tab-more-menu">
                  @if (canPreview(row)) {
                    <button mat-menu-item type="button" data-testid="drawing-set-row-preview" (click)="previewChanges(row)">{{ previewLabel(row) }}</button>
                  }
                  <button mat-menu-item type="button" data-testid="drawing-set-row-edit" (click)="openPage(row)">Edit page</button>
                  <button mat-menu-item type="button" data-testid="drawing-set-row-skip" (click)="setSkip(row, !isSkipped(row))">{{ isSkipped(row) ? 'Include page' : 'Skip page' }}</button>
                  <button mat-menu-item type="button" data-testid="drawing-set-drop" (click)="drop(row)">Drop page</button>
                </mat-menu>
              }
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns" tabindex="0" data-testid="drawing-set-row" [attr.data-uuid]="row.uuid" [attr.aria-label]="'Page ' + row.page_number + (row.number ? ', ' + row.number : '') + ': open'"
            [class.row--attention]="needsAttention(row)" [class.row--skipped]="isSkipped(row)" [class.row--selected]="selectedUuid === row.uuid" [class.row--checked]="isChecked(row)"
            (click)="onRowClick($event, row)" (keydown)="onRowKey($event, row)"></tr>
        </table>
      </div>
    } @else {
      <div class="card-grid" data-testid="drawing-set-grid">
        @for (row of pageRows; track row.uuid) {
          <div class="page-card" role="button" tabindex="0" data-testid="drawing-set-tile" [attr.data-uuid]="row.uuid" [class.page-card--attention]="needsAttention(row)"
            [class.page-card--skipped]="isSkipped(row)" [class.page-card--selected]="selectedUuid === row.uuid" [class.page-card--checked]="isChecked(row)"
            [attr.aria-label]="'Page ' + row.page_number + (row.number ? ', ' + row.number : '') + ': review'" (click)="onRowClick($event, row, 'tile')" (keydown)="onRowKey($event, row)">
            <div class="thumb">
              @if (canBulkEdit) {
                <!-- Item 8: the list's selection on the tile, top left; shown on hover or focus, and on every tile once any page is checked -->
                <span class="tile-check" [class.tile-check--on]="checked.size > 0">
                  <mat-checkbox color="primary" data-testid="drawing-set-tile-check" [aria-label]="'Select page ' + row.page_number" [checked]="isChecked(row)"
                    (mousedown)="checkPress($event)" (keydown)="checkPress($event)" (change)="checkFromBox(row, $event.checked)"></mat-checkbox>
                </span>
              }
              @if (tileImage(row); as src) {
                <img [src]="src" [alt]="'Page ' + row.page_number" loading="lazy" />
              } @else {
                <span class="thumb-placeholder mat-caption">{{ inspecting ? 'Inspecting…' : 'No preview' }}</span>
              }
              <span class="page-no mat-caption">p. {{ row.page_number }}</span>
            </div>
            <div class="card-caption">
              @if (row.number) { <div class="card-number">{{ row.number }}</div> }
              @if (row.title) { <div class="card-subtitle mat-body-1">{{ row.title }}</div> }
              <div class="chip-row">
                <span class="chip">{{ typeLabel(row.sheet_type) }}</span>
                @if (set.status === 'draft') {
                  @if (isSkipped(row)) {
                    <span class="status-badge status-badge--neutral" data-testid="drawing-set-badge-skipped">Skipped</span>
                  } @else if (isFixed(row)) {
                    <span class="status-badge status-badge--success" data-testid="drawing-set-badge-fixed">Fixed</span>
                  } @else if (tileReasonText(row); as short) {
                    <span class="status-badge status-badge--neutral" data-testid="drawing-set-badge-reason" [matTooltip]="reasonText(row)">{{ short }}</span>
                  } @else if (!exceptionsOnly) {
                    <span class="chip" [class.chip--replaces]="row.match.kind === 'replaces'" [attr.data-testid]="row.sheet_pinned ? 'drawing-set-pinned' : null"
                  [matTooltip]="row.sheet_pinned ? 'Pinned to this sheet' : ''" [matTooltipDisabled]="!row.sheet_pinned">{{ matchLabel(row) }}</span>
                  }
                }
              </div>
              @if (warningTexts(row); as warnings) {
                <div class="row-warnings">
                  @for (w of warnings; track w) { <span class="status-badge status-badge--warning" data-testid="drawing-set-warning" [matTooltip]="w">{{ w }}</span> }
                </div>
              }
            </div>
          </div>
        }
      </div>
    }

    @if (visibleRows.length > pageSize) {
      <mat-paginator [length]="visibleRows.length" [pageSize]="pageSize" [pageIndex]="pageIndex" [hidePageSize]="true" (page)="onPage($event)"
        data-testid="drawing-set-paginator"></mat-paginator>
    }
  } @else if (set && !inspecting) {
    <p class="empty-state">No pages in this set.</p>
  }

  @if (!set || set.status === 'draft') {
    <div class="review-footer">
      @if (reviewRow) {
        <button type="button" class="link-button back-to-pages" data-testid="drawing-set-back-to-pages" (click)="backToPages()">Back to pages</button>
      }
      @if (showProblems) {
        <!-- W46: the count of pages to fix, and the banner's Review -->
        <span class="blocked-reason mat-body-1" data-testid="drawing-set-footer-problems">{{ attentionCount }} to fix ·
          <button type="button" class="link-button" data-testid="drawing-set-footer-review" (click)="reviewProblems('footer')">Review</button>
        </span>
      } @else {
        <span class="blocked-reason mat-body-1">{{ publishBlockedReason }}</span>
      }
      <app-primary-button buttonId="drawings-publish-set" data-testid="drawings-publish-set" [isLoading]="publishing" [isDisabled]="!!publishBlockedReason || !canPublish" (handleClick)="publish()">
        {{ publishLabel }}
      </app-primary-button>
    </div>
  }
</div>

<mat-menu #setMenu="matMenu" class="tab-more-menu" xPosition="before">
  <button mat-menu-item type="button" data-testid="drawing-set-discard" (click)="discard()">Discard set</button>
</mat-menu>
